import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { stashPendingSignup } from '../utils/checkoutClient';
import { fetchCsrfToken, authJsonHeaders } from '../utils/authClient';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 800px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  color: #00ff88;
  font-size: 1.2rem;
  margin: 2rem 0;
`;

const NumberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const NumberCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${props => props.selected ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 15px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: #00ff88;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 255, 136, 0.2);
  }

  ${props => props.selected && `
    background: rgba(0, 255, 136, 0.1);
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.3);
  `}
`;

const NumberDisplay = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #00ff88;
  margin-bottom: 0.5rem;
`;

const NumberDetails = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const Price = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const SelectButton = styled.button`
  background: ${props => props.selected ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.selected ? '#000000' : '#ffffff'};
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #00ff88;
    color: #000000;
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 1rem 2rem;
  border: none;
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 255, 136, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  text-align: center;
  margin: 1rem 0;
  padding: 1rem;
  background: rgba(255, 68, 68, 0.1);
  border-radius: 10px;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #00d4ff;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PhoneNumberSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get inmate data from location state
  const inmateData = location.state?.inmateData;

  useEffect(() => {
    if (!inmateData?.inmateConnectionId) {
      navigate('/inmate-signup');
      return;
    }

    fetchAvailableNumbers();
  }, [inmateData, navigate]);

  const fetchAvailableNumbers = async () => {
    try {
      setLoading(true);
      const csrfToken = await fetchCsrfToken();
      const response = await fetch('/api/search-phone-numbers', {
        method: 'POST',
        credentials: 'include',
        headers: authJsonHeaders(csrfToken),
        body: JSON.stringify({
          inmateConnectionId: inmateData.inmateConnectionId,
          areaCode: inmateData.areaCode || '617',
          state: inmateData.state || 'MA'
        }),
      });

      if (response.status === 401) {
        setError('Sign in is required to search phone numbers.');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setAvailableNumbers(data.numbers || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Failed to fetch available numbers');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNumberSelect = (number) => {
    setSelectedNumber(number);
  };

  const handleContinue = () => {
    if (!selectedNumber) {
      setError('Please select a phone number to continue');
      return;
    }

    stashPendingSignup(inmateData, selectedNumber);
    navigate('/payment', {
      state: {
        inmateData,
        selectedNumber
      }
    });
  };

  const formatPhoneNumber = (number) => {
    if (number == null || typeof number !== 'string') return '';
    const cleaned = number.replace(/\D/g, '');
    let national = cleaned;
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      national = cleaned.slice(1);
    }
    if (national.length === 10) {
      return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
    }
    return number;
  };

  if (loading) {
    return (
      <Container>
        <Card>
          <Title>Available Phone Numbers</Title>
          <LoadingSpinner>Searching for available numbers in your area...</LoadingSpinner>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Choose Your Phone Number</Title>
        <Subtitle>Select a phone number for {inmateData?.inmateName}</Subtitle>

        <InfoBox>
          <strong>Important:</strong> Each phone number costs $9.99/month plus applicable taxes. 
          You'll be charged after selecting a number and completing payment.
        </InfoBox>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {availableNumbers.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#cccccc', margin: '2rem 0' }}>
            No available numbers found in your area. Please try a different area code.
          </div>
        ) : (
          <>
            <NumberGrid>
              {availableNumbers.map((number, index) => (
                <NumberCard
                  key={index}
                  selected={selectedNumber?.phoneNumber === number.phoneNumber}
                  onClick={() => handleNumberSelect(number)}
                >
                  <NumberDisplay>{formatPhoneNumber(number.phoneNumber)}</NumberDisplay>
                  <NumberDetails>
                    <div>Area: {number.locality}, {number.region}</div>
                    <div>Type: {number.capabilities?.voice ? 'Voice' : 'N/A'}</div>
                  </NumberDetails>
                  <Price>$9.99/month + taxes</Price>
                  <SelectButton selected={selectedNumber?.phoneNumber === number.phoneNumber}>
                    {selectedNumber?.phoneNumber === number.phoneNumber ? 'Selected' : 'Select'}
                  </SelectButton>
                </NumberCard>
              ))}
            </NumberGrid>

            <ContinueButton 
              onClick={handleContinue}
              disabled={!selectedNumber}
            >
              Continue to Payment
            </ContinueButton>
          </>
        )}
      </Card>
    </Container>
  );
};

export default PhoneNumberSelection; 